/**
 * Geldbeträge in der Oberfläche.
 *
 * Dieselbe Rechnung wie in `api/src/lib/geld.ts`: ganze Cent, kaufmännisch
 * gerundet, niemals Gleitkomma-Euro. Die Wiederholung ist beabsichtigt und
 * hat eine Grenze:
 *
 *   Verbindlich rechnet der Server. Was hier gerechnet wird, ist Vorschau —
 *   die Summe unter einem Rechnungsformular, während getippt wird. Nach dem
 *   Speichern gilt, was die API zurückgibt, und nichts anderes.
 *
 * Der Grund für die Vorschau: Ein Rechnungsformular ohne laufende Summe
 * lässt sich nicht prüfen, und ein Netzaufruf je Tastendruck wäre der
 * falsche Preis dafür.
 */

/** Eingabe → Cent. Wirft bei allem, was kein Betrag ist. */
export function alsCent(wert: string | number): number {
  if (typeof wert === 'number') {
    if (!Number.isFinite(wert)) throw new Error(`Kein gültiger Betrag: ${wert}`);
    return runde(wert * 100);
  }

  let text = wert.trim().replace(/\s|€|EUR/gi, '');
  if (text === '') throw new Error('Leerer Betrag');

  // Ist das letzte Trennzeichen ein Komma, trennt es die Nachkommastellen
  // ("1.234,56"). Sonst ist der Punkt das Dezimaltrennzeichen.
  const letztesKomma = text.lastIndexOf(',');
  const letzterPunkt = text.lastIndexOf('.');

  if (letztesKomma > letzterPunkt) {
    text = text.replaceAll('.', '').replace(',', '.');
  } else {
    text = text.replaceAll(',', '');
  }

  if (!/^-?\d+(\.\d+)?$/.test(text)) throw new Error(`Kein gültiger Betrag: ${wert}`);
  return runde(Number.parseFloat(text) * 100);
}

/** Prüft eine Betragseingabe, ohne zu werfen. */
export function istBetrag(wert: string): boolean {
  try {
    alsCent(wert);
    return true;
  } catch {
    return false;
  }
}

export function runde(cent: number): number {
  const vorzeichen = cent < 0 ? -1 : 1;
  return vorzeichen * Math.round(Math.abs(cent) + Number.EPSILON * Math.abs(cent));
}

/** Cent → Zeichenkette für die API ("1234.56"). */
export function alsBetrag(cent: number): string {
  const vorzeichen = cent < 0 ? '-' : '';
  const absolut = Math.abs(Math.trunc(cent));
  return `${vorzeichen}${Math.floor(absolut / 100)}.${String(absolut % 100).padStart(2, '0')}`;
}

/** Cent → deutsche Anzeige ("1.234,56 €"). */
export function formatiereCent(cent: number, mitWaehrung = true): string {
  const text = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cent / 100);
  return mitWaehrung ? `${text} €` : text;
}

/**
 * Betrag aus der API anzeigen.
 *
 * Beträge kommen als Zeichenkette ("358.80"). Ein leerer oder fehlender
 * Wert wird zu einem Gedankenstrich, nicht zu „0,00 €": Nichts erfasst und
 * null Euro sind zwei verschiedene Aussagen.
 */
export function euro(wert: string | number | null | undefined, mitWaehrung = true): string {
  if (wert === null || wert === undefined || wert === '') return '—';
  try {
    return formatiereCent(alsCent(wert), mitWaehrung);
  } catch {
    return String(wert);
  }
}

export function steuerAusBrutto(bruttoCent: number, satzProzent: number): number {
  if (satzProzent <= 0) return 0;
  return runde((bruttoCent * satzProzent) / (100 + satzProzent));
}

export function nettoAusBrutto(bruttoCent: number, satzProzent: number): number {
  return bruttoCent - steuerAusBrutto(bruttoCent, satzProzent);
}

export function steuerAusNetto(nettoCent: number, satzProzent: number): number {
  if (satzProzent <= 0) return 0;
  return runde((nettoCent * satzProzent) / 100);
}
